import { useState, useEffect, useRef } from 'react';
import './RiskProfileSection.css';

export default function RiskProfileSection({ questions, scoreRanges, profileData, onSave, saving }) {
  const responses = profileData?.responses || {};
  const [localResponses, setLocalResponses] = useState({ ...responses });
  const [showCelebration, setShowCelebration] = useState(false);
  const saveTimerRef = useRef(null);
  const isFirstRender = useRef(true);
  const prevAnsweredRef = useRef(0);

  // Compute live score
  function computeScore(resp) {
    let total = 0;
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const response = resp[question.uid];
      if (!response) continue;

      if (question.question_type === 'multi_choice') {
        const selected = Array.isArray(response) ? response : [];
        for (let j = 0; j < question.options.length; j++) {
          if (selected.includes(question.options[j].uid)) {
            total += question.options[j].score;
          }
        }
      } else {
        for (let j = 0; j < question.options.length; j++) {
          if (question.options[j].uid === response) {
            total += question.options[j].score;
            break;
          }
        }
      }
    }
    return total;
  }

  function computeMaxScore() {
    let max = 0;
    for (let i = 0; i < questions.length; i++) {
      let qMax = 0;
      for (let j = 0; j < questions[i].options.length; j++) {
        if (questions[i].options[j].score > qMax) {
          qMax = questions[i].options[j].score;
        }
      }
      max += qMax;
    }
    return max;
  }

  function computeBand(score) {
    for (let i = 0; i < scoreRanges.length; i++) {
      if (score >= scoreRanges[i].min && score <= scoreRanges[i].max) {
        return scoreRanges[i];
      }
    }
    return null;
  }

  // Auto-save debounce
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(localResponses);
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [localResponses]);

  function handleSingleChoice(questionUid, optionUid) {
    setLocalResponses(prev => ({ ...prev, [questionUid]: optionUid }));
  }

  function handleMultiChoice(questionUid, optionUid, checked) {
    setLocalResponses(prev => {
      const current = Array.isArray(prev[questionUid]) ? [...prev[questionUid]] : [];
      if (checked) {
        if (!current.includes(optionUid)) current.push(optionUid);
      } else {
        const idx = current.indexOf(optionUid);
        if (idx >= 0) current.splice(idx, 1);
      }
      return { ...prev, [questionUid]: current };
    });
  }

  // Group questions by category
  const groups = {};
  const groupOrder = [];
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    const cat = q.category || 'General';
    if (!groups[cat]) {
      groups[cat] = [];
      groupOrder.push(cat);
    }
    groups[cat].push(q);
  }

  const liveScore = computeScore(localResponses);
  const maxScore = computeMaxScore();
  const liveBand = computeBand(liveScore);
  const scorePercent = maxScore > 0 ? Math.round((liveScore / maxScore) * 100) : 0;

  const answeredCount = questions.filter(q => {
    const r = localResponses[q.uid];
    return r && (Array.isArray(r) ? r.length > 0 : r.toString().trim() !== '');
  }).length;

  const allAnswered = questions.length > 0 && answeredCount === questions.length;
  const completionPercent = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  // Celebration trigger
  useEffect(() => {
    if (prevAnsweredRef.current < questions.length && answeredCount === questions.length && questions.length > 0) {
      setShowCelebration(true);
      const t = setTimeout(() => setShowCelebration(false), 2500);
      return () => clearTimeout(t);
    }
    prevAnsweredRef.current = answeredCount;
  }, [answeredCount]);

  return (
    <div className="rp-section">
      {/* Celebration overlay */}
      {showCelebration && (
        <div className="rp-section__celebration">
          <span className="rp-section__celebration-emoji" style={{ left: '10%', animationDelay: '0ms' }}>🎉</span>
          <span className="rp-section__celebration-emoji" style={{ left: '25%', animationDelay: '150ms' }}>🎊</span>
          <span className="rp-section__celebration-emoji" style={{ left: '45%', animationDelay: '50ms' }}>✨</span>
          <span className="rp-section__celebration-emoji" style={{ left: '65%', animationDelay: '200ms' }}>⭐</span>
          <span className="rp-section__celebration-emoji" style={{ left: '80%', animationDelay: '100ms' }}>🌟</span>
          <span className="rp-section__celebration-emoji" style={{ left: '90%', animationDelay: '250ms' }}>💫</span>
          <div className="rp-section__celebration-msg">
            <span className="rp-section__celebration-icon">🎉</span>
            <p>Risk Profile Complete!</p>
          </div>
        </div>
      )}

      {/* Score card */}
      <div className={`rp-section__score-card ${!allAnswered ? 'rp-section__score-card--incomplete' : ''}`}>
        <div className="rp-section__summary-row">
          <div className="rp-section__summary-box">
            <span className="rp-section__summary-label">Risk Score</span>
            {allAnswered ? (
              <span className="rp-section__summary-value">
                {liveScore}<span className="rp-section__summary-max"> / {maxScore}</span>
              </span>
            ) : (
              <span className="rp-section__summary-value rp-section__summary-value--hidden">—</span>
            )}
          </div>
          <div className="rp-section__summary-box rp-section__summary-box--right">
            <span className="rp-section__summary-label">Completion Rate</span>
            <span className="rp-section__summary-value">
              {answeredCount}<span className="rp-section__summary-max"> / {questions.length}</span>
            </span>
            <span className="rp-section__summary-pct">{completionPercent}%</span>
          </div>
        </div>
        <div className="rp-section__score-bar-wrap">
          <div className="rp-section__score-bar" style={{ width: `${allAnswered ? scorePercent : 0}%`, background: liveBand?.color || 'var(--color-primary, #004358)' }} />
        </div>
        <div className="rp-section__score-bands">
          {scoreRanges.map(range => (
            <span
              key={range.label}
              className={`rp-section__band ${allAnswered && liveBand?.label === range.label ? 'rp-section__band--active' : ''}`}
              style={{ '--band-color': range.color || '#004358' }}
            >
              {range.label}
            </span>
          ))}
        </div>
        {!allAnswered && (
          <p className="rp-section__answered">{answeredCount} of {questions.length} questions answered — complete all to reveal your score</p>
        )}
      </div>

      {/* Questions */}
      <div className="rp-section__questions">
        {groupOrder.map(category => (
          <div key={category} className="rp-section__group">
            <h4 className="rp-section__group-title">{category}</h4>
            {groups[category].map((question, qIdx) => {
              const response = localResponses[question.uid];
              const isAnswered = response && (Array.isArray(response) ? response.length > 0 : response.toString().trim() !== '');
              return (
                <div key={question.uid} className={`rp-section__question ${isAnswered ? 'rp-section__question--answered' : 'rp-section__question--unanswered'}`}>
                  <div className="rp-section__q-header">
                    <span className="rp-section__q-num">{qIdx + 1}</span>
                    <p className="rp-section__q-text">{question.question_text}</p>
                    {!isAnswered && <span className="rp-section__not-answered">Not answered</span>}
                  </div>
                  <div className="rp-section__options">
                    {question.options.map(option => {
                      if (question.question_type === 'multi_choice') {
                        const selected = Array.isArray(response) ? response : [];
                        return (
                          <label key={option.uid} className={`rp-section__option ${selected.includes(option.uid) ? 'rp-section__option--selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selected.includes(option.uid)}
                              onChange={e => handleMultiChoice(question.uid, option.uid, e.target.checked)}
                            />
                            <span>{option.text}</span>
                          </label>
                        );
                      } else {
                        return (
                          <label key={option.uid} className={`rp-section__option ${response === option.uid ? 'rp-section__option--selected' : ''}`}>
                            <input
                              type="radio"
                              name={question.uid}
                              checked={response === option.uid}
                              onChange={() => handleSingleChoice(question.uid, option.uid)}
                            />
                            <span>{option.text}</span>
                          </label>
                        );
                      }
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

    </div>
  );
}
